import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FilePen, Mic, Square, Loader2, Trash2, Download, PlusCircle, Save, ChevronRight, MessageCircle, Check, Upload, Paperclip, ChevronDown, History, AlertTriangle, ChevronUp, FileText, Link2, X, User, StickyNote, Calendar as CalendarIcon, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_META = {
  draft:         { label: "Bozza",          cls: "bg-gray-500 text-white" },
  to_send:       { label: "Da mandare",     cls: "bg-yellow-500 text-black" },
  sent:          { label: "Inviato",        cls: "bg-blue-600 text-white" },
  waiting_sign:  { label: "In attesa firma", cls: "bg-[#FF5A1F] text-white" },
  signed:        { label: "Firmato",         cls: "bg-green-600 text-white" },
  rejected:      { label: "Rifiutato",       cls: "bg-red-600 text-white" },
};

const DURATE_OPTIONS = ["3", "6", "9", "12", "18", "24"];

const emptyFields = {
  nome_cognome: "", data_nascita: "", luogo_nascita: "", indirizzo: "",
  citta: "", cap: "", codice_fiscale: "", email: "", telefono: "",
  // Manager di artisti
  is_manager: false,           // se true: contratto/preventivo relativo a un artista gestito
  nome_artista_gestito: "",    // OBBLIGATORIO quando is_manager=true
  importo_totale: "",
  durata_mesi: "12",
  data_inizio_servizi: "",
  services_selected: [],   // array of {id, label}
  services_extra: "",       // free text (one per line)
  piano_pagamento: {
    tipo: "rateale",         // "unica" | "rateale" | "trial_annuale"
    acconto: { importo: "", data: "", paid: false, paid_at: "" },
    rate: [],
    trial: {
      importo_trial: "",
      data_trial: "",
      importo_annuale: "",           // totale annuale scontato (es. 1200)
      modalita_annuale: "rateale",   // "unica" | "rateale"
      data_unica_annuale: "",        // usata se modalita_annuale = unica
      rate_annuale: [],              // rate post-conversione [{importo, data, paid, paid_at}]
      convertito: false,             // flag conversione avvenuta
      data_conversione: "",
      trial_paid: false,             // trial versato
      trial_paid_at: "",
    },
  },
  // Commissions
  commission_pct: "10",     // % base — editable per-contract
  // Historical (imported) contract fields
  is_historical: false,
  contract_date: "",       // data del contratto pregresso (aaaa-mm-gg)
  notes_generali: "",      // note libere (come è stato acquisito, canale, ecc.)
  pdf_original_b64: "",    // set only when importing an existing PDF
  pdf_original_filename: "",
};

// -----------------------------------------------------------------------------
// Commission helpers
// -----------------------------------------------------------------------------
const commissionSummary = (fields) => {
  const pct = parseFloat(fields.commission_pct || "0") || 0;
  const piano = fields.piano_pagamento || {};
  const tipo = piano.tipo || (piano.rate?.length > 0 ? "rateale" : "unica");
  let totalScheduled = 0, totalPaid = 0;

  if (tipo === "trial_annuale") {
    const trial = piano.trial || {};
    const trialAmt = parseFloat(trial.importo_trial || "0") || 0;
    const annualAmt = parseFloat(trial.importo_annuale || "0") || 0;
    // Se convertito: total è l'annuale (che INCLUDE il trial già versato)
    // Se non convertito: total è solo il trial (contratto si chiude)
    totalScheduled = trial.convertito ? annualAmt : trialAmt;
    if (trial.trial_paid) totalPaid += trialAmt;
    if (trial.convertito) {
      (trial.rate_annuale || []).forEach((r) => {
        if (r.paid) totalPaid += parseFloat(r.importo || 0) || 0;
      });
    }
  } else {
    const acc = piano.acconto || {};
    const rate = piano.rate || [];
    const all = [{ ...acc, _label: "Acconto" }, ...rate.map((r, i) => ({ ...r, _label: `Rata ${i + 1}` }))];
    for (const item of all) {
      const amt = parseFloat(item.importo || "0") || 0;
      totalScheduled += amt;
      if (item.paid) totalPaid += amt;
    }
  }
  return {
    pct,
    total_scheduled: totalScheduled,
    total_paid: totalPaid,
    total_pending: Math.max(0, totalScheduled - totalPaid),
    commission_earned: (totalPaid * pct) / 100,
    commission_pending: ((totalScheduled - totalPaid) * pct) / 100,
    commission_total: (totalScheduled * pct) / 100,
  };
};

// Compute overdue installments (unpaid + due date in past)
const collectOverdueRates = (contract) => {
  const f = contract.fields || {};
  const piano = f.piano_pagamento || {};
  const tipo = piano.tipo || (piano.rate?.length > 0 ? "rateale" : "unica");
  const today = new Date().toISOString().slice(0, 10);
  const overdue = [];
  if (tipo === "trial_annuale") {
    const trial = piano.trial || {};
    if (!trial.trial_paid && trial.data_trial && trial.data_trial < today && parseFloat(trial.importo_trial || 0) > 0) {
      overdue.push({ label: "Trial", data: trial.data_trial, importo: trial.importo_trial });
    }
    if (trial.convertito) {
      (trial.rate_annuale || []).forEach((r, i) => {
        if (!r.paid && r.data && r.data < today && parseFloat(r.importo || 0) > 0) {
          overdue.push({ label: `Rata annuale ${i + 1}`, data: r.data, importo: r.importo });
        }
      });
    }
    return overdue;
  }
  const acc = piano.acconto;
  if (acc && !acc.paid && acc.data && acc.data < today && parseFloat(acc.importo || 0) > 0) {
    overdue.push({ label: "Acconto", data: acc.data, importo: acc.importo });
  }
  (piano.rate || []).forEach((r, i) => {
    if (!r.paid && r.data && r.data < today && parseFloat(r.importo || 0) > 0) {
      overdue.push({ label: `Rata ${i + 1}`, data: r.data, importo: r.importo });
    }
  });
  return overdue;
};

const money = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const ANAGRAFICA_LABELS = {
  nome_cognome: "Nome e cognome", data_nascita: "Data di nascita (gg/mm/aaaa)",
  luogo_nascita: "Luogo di nascita", indirizzo: "Indirizzo (via e numero)",
  citta: "Città", cap: "CAP", codice_fiscale: "Codice fiscale",
  email: "Email", telefono: "Cellulare (per WhatsApp)",
};

// ============================================================================
// Art22LivePreview · mirror client-side della logica backend `_build_template_context`
// ----------------------------------------------------------------------------
// Il template Word Art 2.2 ora è 100% DINAMICO: itera `services_by_category` e
// stampa SOLO le voci selezionate dall'utente. Questa preview rispecchia 1:1.
// ============================================================================
function _norm(s) {
  return String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
}

function _substPlaceholders(text, nums) {
  return String(text || "")
    .replace(/\{\{\s*numero_brani[^}]*\}\}/g, nums.numero_brani)
    .replace(/\{\{\s*numero_videoclip[^}]*\}\}/g, nums.numero_videoclip)
    .replace(/\{\{\s*numero_clip_estratte[^}]*\}\}/g, nums.numero_clip_estratte);
}

function _groupSelectedByCategory(selected) {
  const groups = {};
  const order = [];
  const orphans = [];
  (selected || []).forEach((s) => {
    const label = (typeof s === "object" ? s.label : s) || "";
    const cat = (typeof s === "object" ? s.category : "") || "";
    if (!label.trim()) return;
    if (cat.trim()) {
      if (!groups[cat]) { groups[cat] = []; order.push(cat); }
      groups[cat].push(label);
    } else {
      orphans.push(label);
    }
  });
  // Sort categories by leading number (1., 2., 3., ...)
  order.sort((a, b) => {
    const na = /^\d+/.test(a) ? parseInt(a) : 999;
    const nb = /^\d+/.test(b) ? parseInt(b) : 999;
    return na - nb || a.localeCompare(b);
  });
  return { order, groups, orphans };
}

// Flag di comodo per decidere quando mostrare gli input numerici inline.
function computeUpsellFlags(selected) {
  const sel = (selected || []).map((s) => ({
    label: _norm(typeof s === "object" ? s.label : s),
    cat: _norm(typeof s === "object" ? s.category : ""),
  }));
  const anyMatch = (fragments, catFragment = "") => sel.some(({ label, cat }) => {
    if (catFragment && cat.includes(catFragment)) return true;
    return fragments.some((f) => label.includes(f));
  });
  return {
    has_produzione_brani: anyMatch(
      ["produzione completa", "produzione aggiuntiva", "produzione, registrazione, missaggio"],
      "produzione e creazione",
    ),
    has_videoclip: anyMatch(["videoclip"]),
  };
}

function Art22LivePreview({ fields, setField }) {
  const sel = fields?.services_selected || [];
  const flags = React.useMemo(() => computeUpsellFlags(sel), [sel]);
  const nums = React.useMemo(() => ({
    numero_brani: (fields?.numero_brani || "").toString().trim() || "____",
    numero_videoclip: (fields?.numero_videoclip || "").toString().trim()
      || (flags.has_videoclip ? "1" : "____"),
    numero_clip_estratte: (fields?.numero_clip_estratte || "").toString().trim()
      || (flags.has_videoclip ? "5" : "____"),
  }), [fields?.numero_brani, fields?.numero_videoclip, fields?.numero_clip_estratte, flags.has_videoclip]);

  const grouped = React.useMemo(() => _groupSelectedByCategory(sel), [sel]);

  // Servizi extra (textarea libera)
  const extraLines = (fields?.services_extra || "")
    .split("\n")
    .map((r) => r.trim().replace(/^[-•·\t]+\s*/, ""))
    .filter(Boolean);

  // Legacy orphans (vecchi contratti) + extra libero → sezione "Servizi aggiuntivi"
  const extrasForWord = [...grouped.orphans, ...extraLines];

  const totalItems = grouped.order.reduce((a, c) => a + grouped.groups[c].length, 0) + extrasForWord.length;

  return (
    <div className="border-2 border-[#0A0A0A] bg-[#FFF9F3]" data-testid="art22-live-preview">
      <div className="flex items-center justify-between px-3 py-2 bg-[#0A0A0A] text-white">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-bold">Anteprima LIVE · Articolo 2.2</span>
          <span className="text-[9px] uppercase tracking-widest text-emerald-300 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
            Si aggiorna mentre spunti
          </span>
        </div>
        <span className="text-[10px] text-white/60">Ciò che apparirà nel Word · {totalItems} voci</span>
      </div>
      <div className="p-4 text-sm text-[#0A0A0A] leading-relaxed">
        <div className="text-[11px] italic text-[#0A0A0A]/60 mb-3">
          2.2 Il Percorso comprende i seguenti Servizi:
        </div>
        {totalItems === 0 ? (
          <div className="text-[12px] text-[#0A0A0A]/50 italic p-3 border-2 border-dashed border-[#0A0A0A]/20 text-center" data-testid="art22-empty">
            Nessun servizio selezionato. Spunta almeno una voce nelle Macrovoci qui sopra.
          </div>
        ) : (
          <>
            {grouped.order.map((cat) => (
              <div key={cat} className="mb-3" data-testid={`art22-cat-${cat.replace(/\s+/g,"-")}`}>
                <div className="text-[11px] uppercase tracking-wider font-bold text-[#FF5A1F] mb-1">
                  {cat}
                </div>
                <ul className="list-disc pl-5 space-y-1">
                  {grouped.groups[cat].map((lbl, i) => (
                    <li key={`${cat}-${i}`} className="text-[#0A0A0A]/85">
                      {_substPlaceholders(lbl, nums)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {extrasForWord.length > 0 && (
              <div className="mb-2" data-testid="art22-extras">
                <div className="text-[11px] uppercase tracking-wider font-bold text-sky-700 mb-1">
                  Servizi aggiuntivi:
                </div>
                <ul className="list-disc pl-5 space-y-1">
                  {extrasForWord.map((lbl, i) => (
                    <li key={`ex-${i}`} className="text-sky-900/85 italic">
                      {_substPlaceholders(lbl, nums)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
      {/* Mini-form campi numerici — appaiono solo quando l'UPSELL relativo è attivo */}
      {(flags.has_produzione_brani || flags.has_videoclip) && (
        <div className="border-t-2 border-[#0A0A0A] bg-white p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          {flags.has_produzione_brani && (
            <div>
              <label className="text-[9px] uppercase tracking-widest font-bold text-[#FF5A1F]">
                N° brani prodotti
              </label>
              <input
                type="number" min="0" step="1"
                value={fields?.numero_brani || ""}
                onChange={(e) => setField("numero_brani", e.target.value)}
                className="mt-1 w-full border-2 border-[#FF5A1F] p-1.5 text-sm bg-[#FFF5EF] font-bold"
                data-testid="preview-fld-numero-brani"
                placeholder="Es. 4"
              />
            </div>
          )}
          {flags.has_videoclip && (
            <>
              <div>
                <label className="text-[9px] uppercase tracking-widest font-bold text-[#FF5A1F]">
                  N° videoclip
                </label>
                <input
                  type="number" min="0" step="1"
                  value={fields?.numero_videoclip || ""}
                  onChange={(e) => setField("numero_videoclip", e.target.value)}
                  className="mt-1 w-full border-2 border-[#FF5A1F] p-1.5 text-sm bg-[#FFF5EF] font-bold"
                  data-testid="preview-fld-numero-videoclip"
                  placeholder="Default: 1"
                />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-widest font-bold text-[#FF5A1F]">
                  N° clip estratte
                </label>
                <input
                  type="number" min="0" step="1"
                  value={fields?.numero_clip_estratte || ""}
                  onChange={(e) => setField("numero_clip_estratte", e.target.value)}
                  className="mt-1 w-full border-2 border-[#FF5A1F] p-1.5 text-sm bg-[#FFF5EF] font-bold"
                  data-testid="preview-fld-numero-clip-estratte"
                  placeholder="Default: 5"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminContratti() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const importInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/contratti"); setList(r.data || []); }
    catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Prefill from "Il mio giorno" -> Genera contratto
  useEffect(() => {
    const st = location.state || {};
    // Case A: open an existing contract by id (shortcut when contract already exists)
    if (st.open_contract_id) {
      const openId = st.open_contract_id;
      navigate(location.pathname, { replace: true });
      // Load full list, then open the matching one
      (async () => {
        try {
          const r = await api.get("/contratti");
          setList(r.data || []);
          const found = (r.data || []).find((c) => c.id === openId);
          if (found) { setSelected(found); setShowEditor(true); }
          else toast.error("Contratto non trovato");
        } catch { toast.error("Errore caricamento contratto"); }
      })();
      return;
    }
    // Case B: prefill a NEW contract with data from card
    const p = st.prefill;
    if (!p) return;
    setSelected({
      artist_name: p.nome_cognome || "",
      fields: {
        ...emptyFields,
        nome_cognome: p.nome_cognome || "",
        email: p.email || "",
        telefono: p.telefono || "",
        is_manager: !!p.is_manager,
      },
      status: "draft",
    });
    setShowEditor(true);
    navigate(location.pathname, { replace: true });
  }, [location.state]);

  const newContract = () => { setSelected(null); setShowEditor(true); setMenuOpen(false); };
  const newHistoricalManual = () => {
    setSelected({ artist_name: "", fields: { ...emptyFields, is_historical: true }, status: "signed" });
    setShowEditor(true);
    setMenuOpen(false);
  };
  const editContract = (c) => { setSelected(c); setShowEditor(true); };

  const triggerImportPdf = () => { setMenuOpen(false); importInputRef.current?.click(); };

  const handleImportPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Serve un file PDF");
      return;
    }
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/contratti/import-pdf", form, { headers: { "Content-Type": "multipart/form-data" } });
      const parsed = r.data.fields || {};
      setSelected({
        artist_name: parsed.nome_cognome || "",
        fields: {
          ...emptyFields,
          ...parsed,
          is_historical: true,
          pdf_original_b64: r.data.pdf_original_b64 || "",
          pdf_original_filename: r.data.pdf_original_filename || file.name,
        },
        status: "signed",
      });
      setShowEditor(true);
      toast.success("PDF analizzato — controlla i campi estratti");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import PDF fallito");
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const remove = async (cid) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/contratti/${cid}`);
    setList((l) => l.filter((c) => c.id !== cid));
  };

  const downloadOriginal = async (cid, name) => {
    try {
      const res = await api.get(`/contratti/${cid}/attachment`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `contratto_originale_${name || "artista"}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Nessun originale allegato"); }
  };

  const downloadPDF = async (cid, name) => {
    try {
      const res = await api.post(`/contratti/${cid}/generate-pdf`, {}, { responseType: "blob" });
      const isFallback = res.headers?.["x-contract-fallback"] === "docx";
      const ct = (res.headers?.["content-type"] || "").toLowerCase();
      // Verifica che la risposta sia davvero un documento, non HTML/JSON di errore
      const isDoc = ct.includes("application/pdf")
        || ct.includes("officedocument.wordprocessingml")
        || ct.includes("application/octet-stream");
      if (!isDoc) {
        const text = await res.data.text().catch(() => "");
        console.error("Expected pdf/docx, got:", ct, text.slice(0, 500));
        toast.error("Il server non ha restituito un documento valido. Riprova tra qualche secondo.");
        return;
      }
      const ext = isFallback ? "docx" : "pdf";
      const mime = isFallback
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url; a.download = `contratto_${name || "artista"}.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
      if (isFallback) toast.info("PDF non disponibile ora — scaricato Word (aprilo e salvalo come PDF)");
    } catch (e) {
      console.error("downloadPDF error:", e);
      toast.error("Errore generazione documento");
    }
  };

  const downloadDOCX = async (cid, name) => {
    try {
      const res = await api.post(`/contratti/${cid}/generate-docx`, {}, { responseType: "blob" });
      const ct = (res.headers?.["content-type"] || "").toLowerCase();
      // Il backend può ritornare docx (template) o la versione custom caricata
      // dall'utente (potrebbe essere PDF o DOCX). Rispetta l'estensione reale.
      const realExt = (res.headers?.["x-contract-real-ext"] || "").toLowerCase();
      const isDoc = ct.includes("officedocument.wordprocessingml")
        || ct.includes("application/pdf")
        || ct.includes("application/octet-stream");
      if (!isDoc) {
        const text = await res.data.text().catch(() => "");
        console.error("Expected docx/pdf, got:", ct, text.slice(0, 500));
        toast.error("Il server non ha restituito un file valido. Riprova tra qualche secondo (potrebbe essere in fase di deploy).");
        return;
      }
      let ext = realExt || "docx";
      let mime = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      if (ext === "pdf" || ct.includes("application/pdf")) {
        ext = "pdf";
        mime = "application/pdf";
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url; a.download = `contratto_${name || "artista"}.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
      if (ext === "pdf") {
        toast.info("Questo contratto ha una versione PDF caricata, scaricato come PDF");
      }
    } catch (e) {
      console.error("downloadDOCX error:", e);
      toast.error("Errore generazione DOCX");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="contratti-page">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F]">
            <FilePen size={14} /> Admin Privato
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#0A0A0A]">Contratti</h1>
          <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-xl">Genera proposte AI, compila manuale o importa contratti pregressi via PDF (bottone "Carica PDF" dentro ogni contratto). Il piano pagamenti alimenta il calcolo delle provvigioni.</p>
        </div>
        {/* Hidden file input for PDF import */}
        <input
          ref={importInputRef}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => handleImportPdf(e.target.files?.[0])}
          data-testid="import-pdf-input"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDuplicates(true)}
            className="border-2 border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F] px-3 py-2 text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1"
            title="Trova contratti duplicati (nome simile) e fondili in uno solo"
            data-testid="find-duplicates-btn"
          >
            <Link2 size={12} /> Trova duplicati
          </button>
          <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            disabled={importing}
            className="btn-cta disabled:opacity-60"
            data-testid="new-contract-btn"
          >
            {importing ? <Loader2 size={16} className="animate-spin" /> : <PlusCircle size={16} />}
            {importing ? "Analizzo PDF..." : "Nuovo"}
            <ChevronDown size={14} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white border-2 border-[#0A0A0A] shadow-xl z-30" data-testid="new-contract-menu">
              <button onClick={newContract} className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#FFF5EF] border-b border-[#0A0A0A]/10" data-testid="menu-voice">
                <Mic size={16} className="text-[#FF5A1F] mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider">Con voce</div>
                  <div className="text-[10px] text-[#0A0A0A]/60 leading-snug">AI compila i campi da un vocale (per nuovi lead)</div>
                </div>
              </button>
              <button onClick={newHistoricalManual} className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#FFF5EF] border-b border-[#0A0A0A]/10" data-testid="menu-manual">
                <History size={16} className="text-[#FF5A1F] mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider">Manuale (pregresso)</div>
                  <div className="text-[10px] text-[#0A0A0A]/60 leading-snug">Form vuoto per inserire un contratto storico a mano</div>
                </div>
              </button>
              <button onClick={triggerImportPdf} className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#FFF5EF]" data-testid="menu-import">
                <Upload size={16} className="text-[#FF5A1F] mt-0.5 shrink-0" />
                <div>
                  <div className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider">Importa PDF esistente</div>
                  <div className="text-[10px] text-[#0A0A0A]/60 leading-snug">AI estrae anagrafica, importi e piano pagamento dal PDF firmato</div>
                </div>
              </button>
            </div>
          )}
          </div>
        </div>
      </header>

      {showDuplicates && (
        <DuplicatesModal
          onClose={() => setShowDuplicates(false)}
          onMerged={() => { setShowDuplicates(false); load(); }}
        />
      )}

      {loading ? (
        <div className="p-12 text-center text-sm text-[#0A0A0A]/50"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : list.length === 0 ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/30 p-12 text-center text-sm text-[#0A0A0A]/50">
          Nessun contratto ancora. Click "Nuovo contratto" per iniziare.
        </div>
      ) : (
        <>
        <OverdueRatesAlert contracts={list.filter(c => c.status === "signed")} />
        <CommissionSummaryBar contracts={list.filter(c => c.status === "signed")} />
        <div className="space-y-3">
          {list.map((c) => (
            <div key={c.id} className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-4 flex items-center gap-4" data-testid={`contract-${c.id}`}>
              <div className="w-10 h-10 rounded-full bg-[#FF5A1F] text-white flex items-center justify-center font-display text-lg">
                {(c.artist_name||"?").slice(0,1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl uppercase text-[#0A0A0A] flex items-center gap-2 flex-wrap">
                  {c.artist_name}
                  {(c.versions || []).length > 0 && (
                    <span className="text-[9px] uppercase tracking-widest bg-[#0A0A0A] text-white px-2 py-0.5 inline-flex items-center gap-1" title={`${c.versions.length} versione/i personalizzata/e caricata/e — il download restituisce l'ultima`} data-testid={`versions-badge-${c.id}`}>
                      <Paperclip size={9} /> v{c.versions.length}
                    </span>
                  )}
                  {c.fields?.is_manager && (
                    <span className="text-[9px] uppercase tracking-widest bg-[#FF5A1F] text-white px-2 py-0.5" title={`Contratto Manager · Artista gestito: ${c.fields?.nome_artista_gestito || "—"}`} data-testid={`mgr-badge-${c.id}`}>
                      Manager{c.fields?.nome_artista_gestito ? ` · ${c.fields.nome_artista_gestito}` : ""}
                    </span>
                  )}
                  {c.fields?.is_historical && (
                    <span className="text-[9px] uppercase tracking-widest bg-[#0A0A0A] text-[#FAFAF7] px-2 py-0.5" title="Contratto pregresso">Storico</span>
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 flex items-center gap-2 flex-wrap">
                  {(() => {
                    const sel = c.fields?.services_selected || [];
                    const data = c.fields?.contract_date ? ` · ${c.fields.contract_date}` : "";
                    if (sel.length > 0) return <>{`${sel.length} servizi · € ${c.fields?.importo_totale || "—"}${data}`}</>;
                    if (c.fields?.elenco_servizi) return <>{c.fields.elenco_servizi.slice(0, 80)}</>;
                    return <>{`Servizi non definiti${data}`}</>;
                  })()}
                  {(() => {
                    // Provvigioni nel calcolo storico SOLO per contratti firmati
                    if (c.status !== "signed") return null;
                    const s = commissionSummary(c.fields || {});
                    if (!s.total_scheduled) return null;
                    return (
                      <span className="inline-flex items-center gap-1.5 border border-green-600 bg-green-50 text-green-700 px-1.5 py-0.5 normal-case tracking-normal font-bold" title={`Provvigione: ${s.pct}%`} data-testid={`comm-${c.id}`}>
                        💰 {money(s.commission_earned)} / {money(s.commission_total)}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <span className={`px-2 py-1 text-[10px] uppercase tracking-widest ${STATUS_META[c.status]?.cls || "bg-gray-200"}`}>
                {STATUS_META[c.status]?.label || c.status}
              </span>
              {c.sent_at && (
                <span
                  className="px-2 py-1 text-[10px] uppercase tracking-widest border-2 border-green-600 bg-green-50 text-green-700 flex items-center gap-1"
                  title={`Inviato via ${c.sent_via || "whatsapp"} il ${new Date(c.sent_at).toLocaleString("it-IT")}${(c.sent_count||1) > 1 ? ` (${c.sent_count}x)` : ""}`}
                  data-testid={`sent-badge-${c.id}`}
                >
                  <Check size={10} /> Bozza inviata{(c.sent_count||1) > 1 ? ` ${c.sent_count}x` : ""}
                </span>
              )}
              <WhatsAppSendContract contract={c} onSent={load} />
              {c.fields?.pdf_original_b64 ? (
                <button onClick={() => downloadOriginal(c.id, c.artist_name)} className="p-2 border-2 border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F]" title="Scarica PDF originale allegato" data-testid={`orig-${c.id}`}>
                  <Paperclip size={14} />
                </button>
              ) : null}
              <button onClick={() => downloadDOCX(c.id, c.artist_name)} className="p-2 border-2 border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F]" title="Scarica contratto Word (.docx)" data-testid={`docx-${c.id}`}>
                <FileText size={14} />
              </button>
              <button onClick={() => downloadPDF(c.id, c.artist_name)} className="p-2 border-2 border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F]" title="Scarica contratto PDF" data-testid={`pdf-${c.id}`}>
                <Download size={14} />
              </button>
              <button onClick={() => editContract(c)} className="p-2 border-2 border-[#0A0A0A]/30 hover:border-[#0A0A0A]" data-testid={`edit-${c.id}`}>
                <ChevronRight size={14} />
              </button>
              <button onClick={() => remove(c.id)} className="p-2 border-2 border-[#0A0A0A]/30 hover:border-red-500 hover:text-red-500" data-testid={`del-${c.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        </>
      )}

      {showEditor && (
        <ContractEditor initial={selected} onClose={() => { setShowEditor(false); load(); }} />
      )}
    </div>
  );
}

function ClientDossierPanel({ artistName }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    const name = (artistName || "").trim();
    if (!name || name.length < 2) {
      setCards([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/mio-giorno/artist-cards`, { params: { name } })
      .then((r) => {
        if (!cancelled) setCards(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (!cancelled) setCards([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [artistName]);

  const totalNotes = cards.reduce((acc, c) => acc + (c.notes?.length || 0), 0);
  const totalFields = cards.reduce(
    (acc, c) => acc + Object.keys(c.fields || {}).filter((k) => c.fields[k]).length,
    0
  );

  if (!artistName || artistName.trim().length < 2) return null;

  return (
    <div
      className="border-2 border-[#0A0A0A] bg-white"
      data-testid="client-dossier-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-3 bg-[#0A0A0A] text-[#FAFAF7] hover:bg-[#FF5A1F] transition-colors"
        data-testid="dossier-toggle"
      >
        <div className="flex items-center gap-3">
          <User size={18} className="text-[#FF5A1F]" />
          <div className="text-left">
            <div className="text-xs font-bold uppercase tracking-widest">
              Scheda cliente · Il Mio Giorno
            </div>
            <div className="text-[10px] text-[#FAFAF7]/70 mt-0.5">
              {loading
                ? "Caricamento…"
                : cards.length === 0
                ? "Nessuna scheda trovata per questo artista"
                : `${cards.length} scheda${cards.length > 1 ? "e" : ""} · ${totalFields} campi · ${totalNotes} note`}
            </div>
          </div>
        </div>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {open && (
        <div className="p-4 space-y-3 max-h-[380px] overflow-y-auto">
          {/* Shortcut: Apri scheda più recente in Il Mio Giorno */}
          {!loading && cards.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const top = cards[0];
                nav("/admin/mio-giorno", {
                  state: { scroll_to_card: { date: top.date, cardId: top.id } },
                });
                setTimeout(() => {
                  window.dispatchEvent(
                    new CustomEvent("mg-jump-to-card", {
                      detail: { date: top.date, cardId: top.id },
                    })
                  );
                }, 200);
              }}
              className="w-full flex items-center justify-center gap-2 bg-[#FF5A1F] text-white font-bold text-xs uppercase tracking-widest py-2.5 hover:bg-[#0A0A0A] transition-colors border-2 border-[#FF5A1F] hover:border-[#0A0A0A]"
              data-testid="dossier-open-latest"
              title="Apri la scheda più recente nel Mio Giorno per modificare note, stato (es. non interessato) o riprogrammare"
            >
              <ExternalLink size={14} /> Apri scheda nel Mio Giorno
            </button>
          )}
          {loading && (
            <div className="flex items-center gap-2 text-xs text-[#0A0A0A]/60">
              <Loader2 size={14} className="animate-spin" /> Caricamento schede…
            </div>
          )}
          {!loading && cards.length === 0 && (
            <div className="text-xs text-[#0A0A0A]/60 italic">
              Nessuna scheda per <b>{artistName}</b> trovata in "Il Mio Giorno".
              Se sai che esiste con un nome leggermente diverso, aprila da lì e
              usa il pulsante "Unisci con".
            </div>
          )}
          {!loading &&
            cards.map((card) => {
              const fEntries = Object.entries(card.fields || {}).filter(
                ([, v]) => v && String(v).trim()
              );
              return (
                <div
                  key={card.id}
                  className="border border-[#0A0A0A]/20 bg-[#FAFAF7] p-3"
                  data-testid={`dossier-card-${card.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#0A0A0A]">
                      <CalendarIcon size={12} className="text-[#FF5A1F]" />
                      {card.date || "Senza data"}
                      {card.status && (
                        <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-[#0A0A0A] text-white uppercase">
                          {card.status}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        nav("/admin/mio-giorno", {
                          state: { scroll_to_card: { date: card.date, cardId: card.id } },
                        });
                        setTimeout(() => {
                          window.dispatchEvent(
                            new CustomEvent("mg-jump-to-card", {
                              detail: { date: card.date, cardId: card.id },
                            })
                          );
                        }, 200);
                      }}
                      className="text-[11px] font-bold uppercase tracking-wide bg-[#FF5A1F] text-white px-3 py-1.5 hover:bg-[#0A0A0A] transition-colors flex items-center gap-1.5"
                      title="Apri la scheda nel Mio Giorno per modificarla, aggiungere note o cambiare stato"
                      data-testid={`dossier-open-${card.id}`}
                    >
                      Apri scheda <ExternalLink size={11} />
                    </button>
                  </div>

                  {fEntries.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {fEntries.map(([k, v]) => (
                        <div key={k} className="text-xs">
                          <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 font-bold">
                            {k}
                          </div>
                          <div className="text-[#0A0A0A] whitespace-pre-wrap leading-snug">
                            {String(v)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {(card.notes || []).length > 0 && (
                    <div className="border-t border-[#0A0A0A]/10 pt-2 mt-2 space-y-2">
                      <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold flex items-center gap-1">
                        <StickyNote size={11} /> Note ({card.notes.length})
                      </div>
                      {card.notes.map((n, idx) => (
                        <div
                          key={n.id || idx}
                          className="text-xs bg-[#FFF8F3] border-l-2 border-[#FF5A1F] p-2"
                          data-testid={`dossier-note-${card.id}-${idx}`}
                        >
                          {n.transcript && (
                            <div className="text-[#0A0A0A] whitespace-pre-wrap italic">
                              "{n.transcript}"
                            </div>
                          )}
                          {n.text && !n.transcript && (
                            <div className="text-[#0A0A0A] whitespace-pre-wrap">
                              {n.text}
                            </div>
                          )}
                          {n.audio_url && (
                            <audio
                              controls
                              src={n.audio_url}
                              className="mt-1 w-full h-8"
                              style={{ maxWidth: "100%" }}
                            />
                          )}
                          {n.created_at && (
                            <div className="text-[9px] text-[#0A0A0A]/50 mt-1">
                              {new Date(n.created_at).toLocaleString("it-IT")}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {fEntries.length === 0 && (card.notes || []).length === 0 && (
                    <div className="text-[11px] text-[#0A0A0A]/40 italic">
                      Scheda vuota (nessun campo né nota).
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

function ContractEditor({ initial, onClose }) {
  const [artistName, setArtistName] = useState(initial?.artist_name || "");
  const [fields, setFields] = useState(() => mergeFieldsWithDefaults(initial?.fields));
  const [status, setStatus] = useState(initial?.status || "draft");
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [importingPdf, setImportingPdf] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const pdfInputRef = useRef(null);

  useEffect(() => {
    api.get("/contratti/services").then((r) => setServices(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const setPiano = (patch) => setFields((f) => ({ ...f, piano_pagamento: { ...f.piano_pagamento, ...patch } }));

  const toggleService = (svc) => {
    setFields((f) => {
      const cur = f.services_selected || [];
      // Rimuovi sia per id esatto sia per label (gestisce legacy con id diverso ma stessa label)
      const normLbl = (svc.label || "").toLowerCase().replace(/\s+/g, " ").trim();
      const exists = cur.find((s) => s.id === svc.id || (s.label || "").toLowerCase().replace(/\s+/g, " ").trim() === normLbl);
      const next = exists ? cur.filter((s) => s !== exists) : [...cur, { id: svc.id, label: svc.label, category: svc.category || "" }];
      return { ...f, services_selected: next };
    });
  };
  // Confronto anche per label normalizzata: nei contratti VECCHI gli id dei
  // servizi non corrispondono ai nuovi id del catalogo, ma la label spesso sì.
  const isSelected = (id, label) => {
    const sel = fields.services_selected || [];
    if (sel.some((s) => s.id === id)) return true;
    if (!label) return false;
    const norm = (label || "").toLowerCase().replace(/\s+/g, " ").trim();
    return sel.some((s) => (s.label || "").toLowerCase().replace(/\s+/g, " ").trim() === norm);
  };

  // Helper: aggiunge N mesi ad una data ISO (YYYY-MM-DD) gestendo fine mese
  const addMonthsISO = (isoDate, months) => {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split("-").map(Number);
    if (!y || !m || !d) return "";
    // Costruiamo in UTC per evitare TZ shifting
    const dt = new Date(Date.UTC(y, m - 1 + months, 1));
    // Prova il giorno originale; se il mese non ha quel giorno, prendi ultimo giorno del mese
    const targetMonth = dt.getUTCMonth();
    dt.setUTCDate(d);
    if (dt.getUTCMonth() !== targetMonth) {
      // day out of range → torna all'ultimo giorno del mese corretto
      dt.setUTCDate(0);
    }
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  // Ridistribuisce automaticamente importo e date su N rate.
  // - Importo: (totale - acconto) / N con arrotondamento a 2 decimali, ultimo compensa i centesimi
  // - Date: partono dal mese SUCCESSIVO alla data dell'acconto (rata 1 = acconto + 1 mese, ecc.)
  // - Preserva 'paid' e 'paid_at' delle rate esistenti se presenti
  const distributeRate = (num, opts = {}) => {
    const piano = fields.piano_pagamento || {};
    const acconto = piano.acconto || {};
    const totale = parseFloat(String(fields.importo_totale || "").replace(",", ".")) || 0;
    const impAcc = parseFloat(String(acconto.importo || "").replace(",", ".")) || 0;
    const residuo = Math.max(0, totale - impAcc);
    const cur = piano.rate || [];
    const next = [];
    for (let i = 0; i < num; i++) {
      const isLast = i === num - 1;
      // arrotondamento a centesimi; l'ultimo compensa la differenza
      let importo = "";
      if (residuo > 0 && num > 0) {
        const base = Math.round((residuo / num) * 100) / 100;
        importo = isLast
          ? (Math.round((residuo - base * (num - 1)) * 100) / 100).toFixed(2)
          : base.toFixed(2);
      }
      // data: acconto.data + (i+1) mesi
      const data = addMonthsISO(acconto.data || "", i + 1);
      const prev = cur[i] || {};
      next.push({
        importo: opts.keepManualAmounts && prev.importo ? prev.importo : importo,
        data: opts.keepManualDates && prev.data ? prev.data : data,
        paid: !!prev.paid,
        paid_at: prev.paid_at || "",
      });
    }
    setPiano({ rate: next });
  };

  const setNumeroRate = (n) => {
    const num = Math.max(0, Math.min(12, parseInt(n) || 0));
    distributeRate(num);
  };
  const updateRata = (idx, patch) => {
    setPiano({ rate: (fields.piano_pagamento?.rate || []).map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  };

  // Ricalcolo su cambio totale/acconto: preserva quello che l'utente ha toccato manualmente
  const numRateCorrent = (fields.piano_pagamento?.rate || []).length;
  const recomputeRate = () => {
    if (numRateCorrent > 0) distributeRate(numRateCorrent, { keepManualAmounts: false, keepManualDates: false });
  };

  // Auto-ricalcolo: quando l'utente cambia totale/acconto (dopo il mount iniziale)
  // e ha già impostato un numero di rate, ridistribuiamo importi e date in automatico.
  const didMountRateRef = useRef(false);
  useEffect(() => {
    if (!didMountRateRef.current) { didMountRateRef.current = true; return; }
    if (fields.piano_pagamento?.tipo !== "rateale") return;
    const n = (fields.piano_pagamento?.rate || []).length;
    if (n > 0) distributeRate(n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fields.importo_totale,
    fields.piano_pagamento?.acconto?.importo,
    fields.piano_pagamento?.acconto?.data,
  ]);

  // Trial → annuale helpers
  const setTrial = (patch) => setPiano({ trial: { ...(fields.piano_pagamento?.trial || {}), ...patch } });
  const setNumeroRateAnnuale = (n) => {
    const num = Math.max(0, parseInt(n) || 0);
    const cur = fields.piano_pagamento?.trial?.rate_annuale || [];
    let next = cur.slice(0, num);
    while (next.length < num) next.push({ importo: "", data: "", paid: false, paid_at: "" });
    setTrial({ rate_annuale: next });
  };
  const updateRataAnnuale = (idx, patch) => {
    const cur = fields.piano_pagamento?.trial?.rate_annuale || [];
    setTrial({ rate_annuale: cur.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  };
  const setTipoPagamento = (tipo) => setPiano({ tipo });

  const startRec = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(s);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        s.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setProcessing(true);
        const form = new FormData();
        form.append("audio", new File([blob], "voice.webm", { type: "audio/webm" }));
        try {
          const r = await api.post("/contratti/parse-voice", form, { headers: { "Content-Type": "multipart/form-data" } });
          setTranscript(r.data.transcript || "");
          const parsed = r.data.fields || {};
          setFields((f) => {
            const merged = { ...f };
            Object.entries(parsed).forEach(([k, v]) => {
              if (v === null || v === undefined || v === "") return;
              if (k === "services_selected" && Array.isArray(v)) merged[k] = v;
              else if (k === "piano_pagamento" && typeof v === "object") merged[k] = { ...merged.piano_pagamento, ...v };
              else merged[k] = String(v);
            });
            return merged;
          });
          if (parsed.nome_cognome) setArtistName(parsed.nome_cognome);
          toast.success("Campi compilati dal vocale");
        } catch { toast.error("Errore trascrizione"); }
        finally { setProcessing(false); }
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch { toast.error("Microfono non disponibile"); }
  };
  const stopRec = () => { if (mediaRef.current) mediaRef.current.stop(); setRecording(false); };

  const handleImportPdfInEditor = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Serve un file PDF");
      return;
    }
    setImportingPdf(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/contratti/import-pdf", form, { headers: { "Content-Type": "multipart/form-data" } });
      const parsed = r.data.fields || {};
      setFields((f) => {
        const merged = { ...f, is_historical: true };
        Object.entries(parsed).forEach(([k, v]) => {
          if (v === null || v === undefined || v === "") return;
          if (k === "services_selected" && Array.isArray(v)) merged[k] = v;
          else if (k === "piano_pagamento" && typeof v === "object") merged[k] = { ...(merged.piano_pagamento || {}), ...v };
          else merged[k] = String(v);
        });
        merged.pdf_original_b64 = r.data.pdf_original_b64 || "";
        merged.pdf_original_filename = r.data.pdf_original_filename || file.name;
        return merged;
      });
      if (parsed.nome_cognome) setArtistName(parsed.nome_cognome);
      toast.success("PDF analizzato — controlla i campi estratti");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import PDF fallito");
    } finally {
      setImportingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const save = async (download = false, format = "pdf") => {
    // Validazione: contratto Manager richiede nome artista gestito
    if (fields.is_manager && !(fields.nome_artista_gestito || "").trim()) {
      toast.error("Inserisci il nome dell'artista gestito per questo contratto");
      return;
    }
    setSaving(true);
    try {
      // Se manager: componi nome del contratto come "Manager — Artista"
      let finalArtistName = artistName || fields.nome_cognome || "Senza nome";
      if (fields.is_manager && (fields.nome_artista_gestito || "").trim()) {
        const mgr = (artistName || fields.nome_cognome || "").trim();
        const art = fields.nome_artista_gestito.trim();
        finalArtistName = mgr ? `${mgr} — ${art}` : art;
      }
      const payload = { artist_name: finalArtistName, fields, status };
      let id = initial?.id;
      if (id) { await api.patch(`/contratti/${id}`, payload); }
      else { const r = await api.post("/contratti", payload); id = r.data.id; }
      toast.success("Salvato");
      if (download) {
        // Usa SEMPRE generate-from-template così "Salva e genera" rigenera dal
        // template Word fresco, ignorando le versioni caricate (es. PDF uploadati
        // in passato) e evitando il bug "Opzioni filtro ASCII" con file obsoleti.
        const res = await api.post(`/contratti/${id}/generate-from-template?format=${format}`, {}, { responseType: "blob" });
        const isFallback = res.headers?.["x-contract-fallback"] === "docx";
        const ct = (res.headers?.["content-type"] || "").toLowerCase();
        const isDoc = ct.includes("officedocument.wordprocessingml")
          || ct.includes("application/pdf")
          || ct.includes("application/octet-stream");
        if (!isDoc) {
          const text = await res.data.text().catch(() => "");
          console.error("Expected docx/pdf, got:", ct, text.slice(0, 500));
          toast.error("Il server non ha restituito un file valido. Riprova tra qualche secondo.");
          return;
        }
        const ext = (format === "docx" || isFallback) ? "docx" : "pdf";
        const mime = ext === "docx"
          ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          : "application/pdf";
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
        const a = document.createElement("a");
        a.href = url; a.download = `contratto_${(finalArtistName||"artista").replace(/[^a-z0-9]+/gi,"_")}.${ext}`; a.click();
        window.URL.revokeObjectURL(url);
        if (isFallback) toast.info("PDF non disponibile ora — scaricato Word (aprilo e salvalo come PDF)");
      }
      onClose();
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const numeroRate = (fields.piano_pagamento?.rate || []).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="contract-editor">
      <div className="bg-[#FAFAF7] border-4 border-[#0A0A0A] max-w-4xl w-full my-6">
        <div className="flex items-center justify-between p-5 border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7]">
          <div className="font-display text-2xl uppercase">{initial?.id ? "Modifica contratto" : "Nuovo contratto"}</div>
          <button onClick={onClose} className="text-[#FAFAF7]/60 hover:text-[#FF5A1F]" data-testid="close-editor">✕</button>
        </div>

        <div className="p-6 space-y-6">
          {/* Scheda cliente dal Mio Giorno (note, informazioni iniziali) */}
          <ClientDossierPanel artistName={artistName} />

          {/* Voice recorder */}
          <div className="border-2 border-[#0A0A0A] p-4 bg-[#F2F2EC]">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">Compilazione rapida via voce</div>
                <div className="text-xs text-[#0A0A0A]/60 mt-1">Registra un messaggio con nome artista, servizi, importo, durata, pagamento — l'AI compila i campi.</div>
              </div>
              {!recording ? (
                <button onClick={startRec} disabled={processing} className="btn-cta disabled:opacity-60" data-testid="rec-start">
                  {processing ? <Loader2 size={16} className="animate-spin"/> : <Mic size={16}/>}
                  {processing ? "Trascrivo..." : "Registra"}
                </button>
              ) : (
                <button onClick={stopRec} className="btn-cta bg-red-600 border-red-700 animate-pulse" data-testid="rec-stop">
                  <Square size={16}/> Stop
                </button>
              )}
            </div>
            {transcript && <div className="text-xs italic text-[#0A0A0A]/70 border-t pt-2 mt-2">"{transcript}"</div>}
          </div>

          {/* Historical toggle + contract date + notes */}
          <div className="border-2 border-[#0A0A0A]/20 p-4 bg-[#FAFAF7]">
            <input
              ref={pdfInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => handleImportPdfInEditor(e.target.files?.[0])}
              data-testid="editor-pdf-input"
            />
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <label className="flex items-start gap-3 cursor-pointer flex-1 min-w-[240px]" data-testid="historical-toggle">
                <div className={`w-5 h-5 border-2 shrink-0 flex items-center justify-center mt-0.5 ${fields.is_historical ? "bg-[#FF5A1F] border-[#FF5A1F]" : "border-[#0A0A0A]/40"}`}>
                  {fields.is_historical && <Check size={14} className="text-white" />}
                </div>
                <input type="checkbox" checked={!!fields.is_historical} onChange={(e)=>set("is_historical", e.target.checked)} className="sr-only" />
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]">Contratto pregresso (storico)</div>
                  <div className="text-[10px] text-[#0A0A0A]/60 mt-0.5">Spunta se stai inserendo un artista già acquisito in passato. Utile per il calcolo provvigioni.</div>
                </div>
              </label>
              <button
                type="button"
                onClick={() => pdfInputRef.current?.click()}
                disabled={importingPdf}
                className="px-3 py-2 border-2 border-[#FF5A1F] bg-[#FF5A1F] text-white text-[10px] uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:border-[#0A0A0A] flex items-center gap-2 disabled:opacity-60"
                title="L'AI legge il PDF del contratto già firmato e compila tutti i campi"
                data-testid="editor-import-pdf-btn"
              >
                {importingPdf ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {importingPdf ? "Analizzo PDF..." : "Carica PDF esistente"}
              </button>
            </div>
            {fields.is_historical && (
              <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-[#0A0A0A]/10">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Data contratto (pregresso)</label>
                  <input type="date" value={fields.contract_date || ""} onChange={(e)=>set("contract_date", e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid="fld-contract-date"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Note generali</label>
                  <input value={fields.notes_generali || ""} onChange={(e)=>set("notes_generali", e.target.value)} placeholder="es. Acquisito via Instagram, referral da..." className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid="fld-notes-generali"/>
                </div>
                {fields.pdf_original_filename && (
                  <div className="md:col-span-2 flex items-center gap-2 text-[11px] text-[#0A0A0A]/70">
                    <Paperclip size={14} className="text-[#FF5A1F]" />
                    <span>PDF allegato: <b>{fields.pdf_original_filename}</b> — verrà salvato con il contratto</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Manager di artisti */}
          <div className={`border-2 p-4 ${fields.is_manager ? "border-[#FF5A1F] bg-[#FFF5EF]" : "border-[#0A0A0A]/20 bg-[#FAFAF7]"}`}>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="manager-toggle">
              <div className={`w-5 h-5 border-2 shrink-0 flex items-center justify-center mt-0.5 ${fields.is_manager ? "bg-[#FF5A1F] border-[#FF5A1F]" : "border-[#0A0A0A]/40"}`}>
                {fields.is_manager && <Check size={14} className="text-white" />}
              </div>
              <input type="checkbox" checked={!!fields.is_manager} onChange={(e)=>set("is_manager", e.target.checked)} className="sr-only" />
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]">Contratto per Manager di artisti</div>
                <div className="text-[10px] text-[#0A0A0A]/60 mt-0.5">Attiva se questa persona rappresenta più artisti. Puoi creare un contratto/preventivo separato per ciascun artista gestito.</div>
              </div>
            </label>
            {fields.is_manager && (
              <div className="mt-4 pt-4 border-t border-[#FF5A1F]/30">
                <label className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">
                  Nome artista gestito <span className="text-red-600">*</span>
                </label>
                <input
                  value={fields.nome_artista_gestito || ""}
                  onChange={(e)=>set("nome_artista_gestito", e.target.value)}
                  placeholder="Es. Marco Blu (uno dei suoi artisti)"
                  className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm"
                  data-testid="fld-nome-artista-gestito"
                />
                <div className="text-[10px] text-[#0A0A0A]/60 mt-1">Serve a distinguere i vari contratti/preventivi del manager. Verrà usato come suffisso nel nome del contratto.</div>
              </div>
            )}
          </div>

          {/* Anagrafica */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F] mb-3">{fields.is_manager ? "Anagrafica manager" : "Anagrafica artista"}</div>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.keys(ANAGRAFICA_LABELS).map((k) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">{ANAGRAFICA_LABELS[k]}</label>
                  <input value={fields[k] || ""} onChange={(e)=>set(k, e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid={`fld-${k}`}/>
                </div>
              ))}
            </div>
          </div>

          {/* Servizi (checkbox multi) */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">Servizi del percorso (Art. 2.2)</div>
              {services.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => set("services_selected", services.map((s) => ({ id: s.id, label: s.label })))}
                    className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 border border-[#FF5A1F] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white transition"
                    data-testid="svc-select-all"
                  >
                    Seleziona tutti
                  </button>
                  <button
                    type="button"
                    onClick={() => set("services_selected", [])}
                    className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 border border-[#0A0A0A]/50 text-[#0A0A0A]/70 hover:bg-[#0A0A0A] hover:text-white transition"
                    data-testid="svc-deselect-all"
                  >
                    Deseleziona
                  </button>
                </div>
              )}
            </div>
            {services.length === 0 ? (
              <div className="text-xs text-[#0A0A0A]/50 italic">Nessun servizio in catalogo. Chiedi all'admin di aggiungerne in "Servizi contratto".</div>
            ) : (
              <div className="space-y-4 max-h-[32rem] overflow-y-auto border-2 border-[#0A0A0A]/20 p-3 bg-white">
                {(() => {
                  // Raggruppa per category (preservando ordine).
                  // I servizi legacy senza category ("Altri servizi") sono NASCOSTI dalla UI:
                  // sono duplicati dalle 5 Macrovoci. Restano nel DB per retro-compatibilità
                  // con i contratti pregressi (che li vedono nella sezione "Servizi ereditati").
                  const groups = {};
                  const order = [];
                  services.forEach((s) => {
                    const rawCat = (s.category || "").trim();
                    if (!rawCat) return; // skip legacy senza categoria
                    const cat = rawCat;
                    if (!groups[cat]) { groups[cat] = []; order.push(cat); }
                    groups[cat].push(s);
                  });
                  // Sort categories: quelle che iniziano con numero per prime, in ordine naturale
                  order.sort((a, b) => {
                    const na = /^\d+/.test(a) ? parseInt(a) : 999;
                    const nb = /^\d+/.test(b) ? parseInt(b) : 999;
                    return na - nb || a.localeCompare(b);
                  });
                  return order.map((cat) => {
                    const items = groups[cat];
                    const allSelected = items.every((s) => isSelected(s.id, s.label));
                    const someSelected = items.some((s) => isSelected(s.id, s.label));
                    const toggleGroup = () => {
                      const current = fields.services_selected || [];
                      if (allSelected) {
                        // Deseleziona tutti del gruppo (match per id OR label)
                        const normLabels = new Set(items.map((s) => (s.label || "").toLowerCase().replace(/\s+/g, " ").trim()));
                        const ids = new Set(items.map((s) => s.id));
                        set("services_selected", current.filter((x) => !ids.has(x.id) && !normLabels.has((x.label || "").toLowerCase().replace(/\s+/g, " ").trim())));
                      } else {
                        // Seleziona tutti del gruppo mantenendo gli altri (dedup by id+label)
                        const existingIds = new Set(current.map((x) => x.id));
                        const existingLabels = new Set(current.map((x) => (x.label || "").toLowerCase().replace(/\s+/g, " ").trim()));
                        const toAdd = items
                          .filter((s) => !existingIds.has(s.id) && !existingLabels.has((s.label || "").toLowerCase().replace(/\s+/g, " ").trim()))
                          .map((s) => ({ id: s.id, label: s.label, category: s.category }));
                        set("services_selected", [...current, ...toAdd]);
                      }
                    };
                    return (
                      <div key={cat} className="border-2 border-[#0A0A0A]/20" data-testid={`svc-cat-${cat.replace(/\s+/g,"-")}`}>
                        <div className={`flex items-center justify-between gap-2 p-2 ${allSelected ? "bg-[#FF5A1F] text-white" : someSelected ? "bg-orange-100 text-[#0A0A0A]" : "bg-[#F2F2EC] text-[#0A0A0A]"}`}>
                          <div className="text-[11px] uppercase tracking-wider font-bold">{cat}</div>
                          <button
                            type="button"
                            onClick={toggleGroup}
                            className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 border-2 transition ${allSelected ? "border-white bg-white text-[#FF5A1F] hover:bg-transparent hover:text-white" : "border-[#FF5A1F] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white"}`}
                            data-testid={`svc-cat-toggle-${cat.replace(/\s+/g,"-")}`}
                          >
                            {allSelected ? "Deseleziona tutte" : "Seleziona tutte"}
                          </button>
                        </div>
                        <div className="p-2 space-y-1">
                          {items.map((s) => {
                            const sel = isSelected(s.id, s.label);
                            return (
                              <label key={s.id} className={`flex items-start gap-2 p-2 cursor-pointer border-2 transition-colors ${sel ? "border-[#FF5A1F] bg-[#FFF5EF]" : "border-transparent hover:bg-[#F2F2EC]"}`} data-testid={`svc-check-${s.id}`}>
                                <div className={`w-5 h-5 border-2 shrink-0 flex items-center justify-center mt-0.5 ${sel ? "bg-[#FF5A1F] border-[#FF5A1F]" : "border-[#0A0A0A]/40"}`}>
                                  {sel && <Check size={14} className="text-white" />}
                                </div>
                                <input type="checkbox" checked={sel} onChange={() => toggleService(s)} className="sr-only" />
                                <span className="text-sm text-[#0A0A0A]/85 leading-snug">{s.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
            {/* Servizi ereditati dal contratto originale (orfani: in services_selected ma
                NON più nel catalogo attuale né per id né per label). Permette di vederli
                comunque e di deselezionarli uno a uno. */}
            {(() => {
              const sel = fields.services_selected || [];
              if (sel.length === 0 || services.length === 0) return null;
              const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();
              const catIds = new Set(services.map((s) => s.id));
              const catLabels = new Set(services.map((s) => norm(s.label)));
              const orphans = sel.filter((s) => !catIds.has(s.id) && !catLabels.has(norm(s.label)));
              if (orphans.length === 0) return null;
              return (
                <div className="mt-4 border-2 border-amber-500 bg-amber-50 p-3" data-testid="legacy-services">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 mb-2">
                    Servizi ereditati dal contratto originale ({orphans.length})
                  </div>
                  <div className="text-[11px] text-amber-900/70 mb-2">
                    Questi servizi sono stati scelti al momento della creazione del contratto ma non sono (più) nel catalogo attuale.
                    Verranno comunque mostrati nel documento Word in sezione <b>"ALTRI SERVIZI"</b>. Per sostituirli con una delle 4 Macrovoci, deselezionali qui sotto e seleziona i nuovi dal catalogo sopra.
                  </div>
                  <div className="space-y-1">
                    {orphans.map((o, i) => (
                      <label key={`${o.id}-${i}`} className="flex items-start gap-2 p-2 bg-white border border-amber-400 cursor-pointer hover:bg-amber-100" data-testid={`legacy-svc-${i}`}>
                        <div className="w-5 h-5 bg-[#FF5A1F] border-2 border-[#FF5A1F] shrink-0 flex items-center justify-center mt-0.5">
                          <Check size={14} className="text-white" />
                        </div>
                        <input
                          type="checkbox"
                          checked
                          onChange={() => {
                            const cur = fields.services_selected || [];
                            set("services_selected", cur.filter((x) => x !== o));
                          }}
                          className="sr-only"
                        />
                        <span className="text-sm text-[#0A0A0A]/85 leading-snug">{o.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })()}
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Servizi aggiuntivi (uno per riga, testo libero)</label>
              <textarea value={fields.services_extra || ""} onChange={(e)=>set("services_extra", e.target.value)} rows={3} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm resize-none" placeholder="Es. Produzione videoclip per singolo di lancio" data-testid="fld-services-extra"/>
            </div>
          </div>

          {/* ============================================================
              LIVE PREVIEW · Articolo 2.2 del contratto
              Mirror esatto della logica backend `_build_template_context`.
              Mostra le 13 righe base (sempre) + 5 UPSELL conditional.
          ============================================================ */}
          <Art22LivePreview fields={fields} setField={set} />

          {/* Importo + Durata + Data inizio + Provvigione % */}
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Importo totale IVA inclusa (€)</label>
              <input value={fields.importo_totale || ""} onChange={(e)=>set("importo_totale", e.target.value)} placeholder="es. 1500" className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid="fld-importo"/>
              {(() => {
                // Somma guida: parsa i prezzi "+€500", "+€1.000", "+€3.000" dai label degli extra selezionati
                const sel = fields.services_selected || [];
                let sumExtras = 0;
                for (const s of sel) {
                  const lbl = (s.label || "");
                  const m = lbl.match(/\+\s*€\s*([\d\.]+)/);
                  if (m) {
                    const n = parseFloat(m[1].replace(/\./g, ""));
                    if (!isNaN(n)) sumExtras += n;
                  }
                }
                if (sumExtras <= 0) return null;
                return (
                  <div className="mt-1 text-[10px] text-[#FF5A1F] font-bold" data-testid="extras-sum-hint">
                    Extra selezionati: +€ {sumExtras.toLocaleString("it-IT")}
                  </div>
                );
              })()}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Durata (mesi)</label>
              <select value={fields.durata_mesi || ""} onChange={(e)=>set("durata_mesi", e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm bg-white" data-testid="fld-durata">
                <option value="">— seleziona —</option>
                {DURATE_OPTIONS.map((d) => <option key={d} value={d}>{d} mesi</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Data inizio prestazioni</label>
              <input type="date" value={fields.data_inizio_servizi || ""} onChange={(e)=>set("data_inizio_servizi", e.target.value)} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid="fld-inizio"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60 flex items-center gap-1">
                Numero brani prodotti
                <span className="text-[8px] text-[#0A0A0A]/40">sez. 4 contratto</span>
              </label>
              <input
                type="number"
                min="0" step="1"
                value={fields.numero_brani || ""}
                onChange={(e) => set("numero_brani", e.target.value)}
                className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm"
                data-testid="fld-numero-brani"
                placeholder="Es. 4"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60 flex items-center gap-1">
                Provvigione (%)
                <span className="text-[8px] text-[#0A0A0A]/40">solo su rate incassate</span>
              </label>
              <input
                type="number"
                min="0" max="100" step="0.5"
                value={fields.commission_pct || ""}
                onChange={(e)=>set("commission_pct", e.target.value)}
                placeholder="10"
                className="mt-1 w-full border-2 border-[#FF5A1F] p-2 text-sm bg-[#FFF5EF] font-bold"
                data-testid="fld-commission-pct"
              />
            </div>
          </div>

          {/* Piano pagamenti + Provvigioni */}
          <div className="border-2 border-[#0A0A0A]/20 p-4 bg-[#FAFAF7]">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">Piano di pagamento</div>
              <CommissionBadge fields={fields} />
            </div>

            {/* Selettore modalità di pagamento */}
            {(() => {
              const tipo = fields.piano_pagamento?.tipo || "rateale";
              const trial = fields.piano_pagamento?.trial || {};
              const numeroRateAnnuale = (trial.rate_annuale || []).length;
              return (
                <>
                  <div className="mb-4">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60 mb-2 block">Modalità di pagamento (Art. 3.2)</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { k: "unica", l: "Unica soluzione" },
                        { k: "rateale", l: "Rateale" },
                        { k: "acconto_blocco", l: "Acconto di blocco" },
                        { k: "trial_annuale", l: "Prova mensile + conversione annuale" },
                      ].map((o) => (
                        <button
                          key={o.k}
                          type="button"
                          onClick={() => setTipoPagamento(o.k)}
                          className={`px-3 py-2 text-[10px] uppercase tracking-widest border-2 font-bold transition-colors ${
                            tipo === o.k
                              ? "bg-[#FF5A1F] border-[#FF5A1F] text-white"
                              : "border-[#0A0A0A]/30 text-[#0A0A0A]/60 hover:border-[#FF5A1F]"
                          }`}
                          data-testid={`pay-tipo-${o.k}`}
                        >
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* --- MODALITÀ UNICA + RATEALE + ACCONTO DI BLOCCO (acconto + rate) --- */}
                  {tipo !== "trial_annuale" && (
                    <>
                      {tipo === "acconto_blocco" && (
                        <div className="mb-4 bg-indigo-50 border-l-4 border-indigo-600 p-3 text-[12px] text-[#0A0A0A]/80">
                          <b className="text-indigo-700">Acconto di blocco.</b> L'artista versa un acconto per
                          riservare l'offerta. Il saldo residuo (€ {(() => {
                            try {
                              const tot = parseFloat(String(fields.importo_totale || "0").replace(",", ".")) || 0;
                              const acc = parseFloat(String(fields.piano_pagamento?.acconto?.importo || "0").replace(",", ".")) || 0;
                              return (tot - acc).toLocaleString("it-IT", { minimumFractionDigits: 2 });
                            } catch { return "—"; }
                          })()}) verrà corrisposto <b>in data e con modalità che l'artista comunicherà</b> prima dell'inizio delle Prestazioni.
                        </div>
                      )}
                      <div className={`grid gap-3 mb-4 ${tipo === "acconto_blocco" ? "md:grid-cols-[2fr_2fr_1.5fr]" : "md:grid-cols-[2fr_2fr_1fr_1.5fr]"}`}>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">
                            {tipo === "acconto_blocco" ? "Importo acconto di blocco (€)" : "Acconto (€)"}
                          </label>
                          <input
                            value={fields.piano_pagamento?.acconto?.importo || ""}
                            onChange={(e)=>setPiano({ acconto: { ...(fields.piano_pagamento?.acconto||{}), importo: e.target.value } })}
                            placeholder={tipo === "acconto_blocco" ? "Es. 500" : "0 = nessun acconto"}
                            className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm"
                            data-testid="fld-acconto-importo"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Data acconto</label>
                          <input
                            type="date"
                            value={fields.piano_pagamento?.acconto?.data || ""}
                            onChange={(e)=>setPiano({ acconto: { ...(fields.piano_pagamento?.acconto||{}), data: e.target.value } })}
                            className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm"
                            data-testid="fld-acconto-data"
                          />
                        </div>
                        {tipo !== "acconto_blocco" && (
                          <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Numero rate</label>
                            <select
                              value={numeroRate}
                              onChange={(e)=>setNumeroRate(e.target.value)}
                              className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm bg-white"
                              data-testid="fld-numero-rate"
                              disabled={tipo === "unica"}
                            >
                              {[0,1,2,3,4,5,6,7,8,9,10,11,12].map((n) => <option key={n} value={n}>{n === 0 ? "Solo acconto" : `${n} rat${n===1?"a":"e"}`}</option>)}
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">
                            {tipo === "acconto_blocco" ? "Acconto incassato?" : "Acconto incassato?"}
                          </label>
                          <PaidToggle
                            paid={!!fields.piano_pagamento?.acconto?.paid}
                            onToggle={(v) => setPiano({ acconto: { ...(fields.piano_pagamento?.acconto||{}), paid: v, paid_at: v ? new Date().toISOString() : "" } })}
                            testId="acconto-paid"
                          />
                        </div>
                      </div>
                      {tipo === "rateale" && numeroRate > 0 && (
                        <div className="space-y-2">
                          <div className="text-[10px] text-[#0A0A0A]/60 italic bg-[#FFF5EF] border-l-2 border-[#FF5A1F] p-2">
                            📅 Le rate sono auto-calcolate: <b>(totale − acconto) / {numeroRate}</b>, con scadenze <b>mensili</b> a partire da 1 mese dopo la data dell'acconto. Modificare totale/acconto ricalcola tutte le rate. Puoi comunque editare importi e date manualmente qui sotto.
                          </div>
                          {(fields.piano_pagamento?.rate || []).map((r, i) => (
                            <div key={i} className={`grid md:grid-cols-[80px_1fr_1fr_1.5fr] gap-3 items-end p-2 ${r.paid ? "bg-green-50 border-l-4 border-green-500" : ""}`} data-testid={`rata-${i+1}`}>
                              <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60 pb-3">Rata {i + 1}</div>
                              <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Importo (€)</label>
                                <input value={r.importo || ""} onChange={(e)=>updateRata(i, { importo: e.target.value })} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid={`rata-importo-${i+1}`}/>
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Data scadenza</label>
                                <input type="date" value={r.data || ""} onChange={(e)=>updateRata(i, { data: e.target.value })} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid={`rata-data-${i+1}`}/>
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Incassata?</label>
                                <PaidToggle
                                  paid={!!r.paid}
                                  onToggle={(v) => updateRata(i, { paid: v, paid_at: v ? new Date().toISOString() : "" })}
                                  testId={`rata-paid-${i+1}`}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* --- MODALITÀ TRIAL → ANNUALE --- */}
                  {tipo === "trial_annuale" && (
                    <div className="space-y-4">
                      <div className="bg-[#FFF5EF] border-l-4 border-[#FF5A1F] p-3 text-[11px] text-[#0A0A0A]/80">
                        <b>Prova mensile con opzione conversione annuale.</b> L'artista paga il mese di prova. Se converte entro la scadenza, passa all'annuale scontato (l'importo annuale INCLUDE il trial già versato). Se non converte, il contratto si chiude col trial incassato.
                      </div>
                      <div className="grid md:grid-cols-[2fr_2fr_1.5fr] gap-3">
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Importo mese di prova (€)</label>
                          <input value={trial.importo_trial || ""} onChange={(e)=>setTrial({ importo_trial: e.target.value })} placeholder="es. 250" className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid="fld-trial-importo"/>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Data pagamento trial</label>
                          <input type="date" value={trial.data_trial || ""} onChange={(e)=>setTrial({ data_trial: e.target.value })} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid="fld-trial-data"/>
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Trial incassato?</label>
                          <PaidToggle
                            paid={!!trial.trial_paid}
                            onToggle={(v) => setTrial({ trial_paid: v, trial_paid_at: v ? new Date().toISOString() : "" })}
                            testId="trial-paid"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Importo annuale scontato (€) — <span className="text-[9px] text-[#0A0A0A]/50">se converte, totale onnicomprensivo</span></label>
                        <input value={trial.importo_annuale || ""} onChange={(e)=>setTrial({ importo_annuale: e.target.value })} placeholder="es. 1200" className="mt-1 w-full max-w-[300px] border-2 border-[#0A0A0A] p-2 text-sm" data-testid="fld-trial-annuale"/>
                      </div>

                      {/* Sezione conversione */}
                      <div className={`border-2 p-3 ${trial.convertito ? "border-green-600 bg-green-50" : "border-[#0A0A0A]/20 bg-[#FAFAF7]"}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <div className={`w-5 h-5 border-2 shrink-0 flex items-center justify-center mt-0.5 ${trial.convertito ? "bg-green-600 border-green-600" : "border-[#0A0A0A]/40"}`}>
                            {trial.convertito && <Check size={14} className="text-white" />}
                          </div>
                          <input type="checkbox" checked={!!trial.convertito} onChange={(e)=>setTrial({ convertito: e.target.checked, data_conversione: e.target.checked ? (trial.data_conversione || new Date().toISOString().slice(0,10)) : "" })} className="sr-only" data-testid="fld-trial-convertito"/>
                          <div className="flex-1">
                            <div className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]">Convertito in annuale</div>
                            <div className="text-[10px] text-[#0A0A0A]/60 mt-0.5">Spunta quando l'artista conferma di voler proseguire con l'annuale. Il piano rate si aggiornerà automaticamente.</div>
                          </div>
                        </label>
                        {trial.convertito && (
                          <div className="mt-4 pt-4 border-t border-green-600/30 space-y-3">
                            <div className="grid md:grid-cols-[1fr_1fr] gap-3">
                              <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Data conversione</label>
                                <input type="date" value={trial.data_conversione || ""} onChange={(e)=>setTrial({ data_conversione: e.target.value })} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid="fld-trial-data-conv"/>
                              </div>
                              <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Modalità pagamento annuale</label>
                                <div className="mt-1 flex gap-2">
                                  <button type="button" onClick={()=>setTrial({ modalita_annuale: "unica" })} className={`flex-1 px-3 py-2 text-[10px] uppercase tracking-widest border-2 font-bold ${trial.modalita_annuale === "unica" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]/30"}`} data-testid="trial-mod-unica">Unica</button>
                                  <button type="button" onClick={()=>setTrial({ modalita_annuale: "rateale" })} className={`flex-1 px-3 py-2 text-[10px] uppercase tracking-widest border-2 font-bold ${trial.modalita_annuale === "rateale" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]/30"}`} data-testid="trial-mod-rateale">Rateale</button>
                                </div>
                              </div>
                            </div>
                            {trial.modalita_annuale === "unica" && (
                              <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Data pagamento annuale (residuo)</label>
                                <input type="date" value={trial.data_unica_annuale || ""} onChange={(e)=>setTrial({ data_unica_annuale: e.target.value })} className="mt-1 w-full max-w-[300px] border-2 border-[#0A0A0A] p-2 text-sm" data-testid="fld-trial-unica-data"/>
                                <div className="text-[10px] text-[#0A0A0A]/50 mt-1">Residuo da pagare: € {Math.max(0, (parseFloat(trial.importo_annuale||0)||0) - (parseFloat(trial.importo_trial||0)||0)).toFixed(2)}</div>
                              </div>
                            )}
                            {trial.modalita_annuale === "rateale" && (
                              <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Numero rate annuali</label>
                                <select value={numeroRateAnnuale} onChange={(e)=>setNumeroRateAnnuale(e.target.value)} className="mt-1 w-full max-w-[200px] border-2 border-[#0A0A0A] p-2 text-sm bg-white" data-testid="fld-numero-rate-annuale">
                                  {[0,1,2,3,4,5,6,9,11,12].map((n) => <option key={n} value={n}>{n === 0 ? "0 rate" : `${n} rat${n===1?"a":"e"}`}</option>)}
                                </select>
                                <div className="text-[10px] text-[#0A0A0A]/50 mt-1">Residuo totale: € {Math.max(0, (parseFloat(trial.importo_annuale||0)||0) - (parseFloat(trial.importo_trial||0)||0)).toFixed(2)}</div>
                                {numeroRateAnnuale > 0 && (
                                  <div className="mt-3 space-y-2">
                                    {(trial.rate_annuale || []).map((r, i) => (
                                      <div key={i} className={`grid md:grid-cols-[80px_1fr_1fr_1.5fr] gap-3 items-end p-2 ${r.paid ? "bg-green-50 border-l-4 border-green-500" : ""}`} data-testid={`rata-ann-${i+1}`}>
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60 pb-3">Rata {i + 1}</div>
                                        <div>
                                          <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Importo (€)</label>
                                          <input value={r.importo || ""} onChange={(e)=>updateRataAnnuale(i, { importo: e.target.value })} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid={`rata-ann-importo-${i+1}`}/>
                                        </div>
                                        <div>
                                          <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Data scadenza</label>
                                          <input type="date" value={r.data || ""} onChange={(e)=>updateRataAnnuale(i, { data: e.target.value })} className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm" data-testid={`rata-ann-data-${i+1}`}/>
                                        </div>
                                        <div>
                                          <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Incassata?</label>
                                          <PaidToggle
                                            paid={!!r.paid}
                                            onToggle={(v) => updateRataAnnuale(i, { paid: v, paid_at: v ? new Date().toISOString() : "" })}
                                            testId={`rata-ann-paid-${i+1}`}
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="pt-2">
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!initial?.id) { toast.error("Salva il contratto prima"); return; }
                                  if (!window.confirm("Crea un nuovo contratto annuale collegato a questo trial?")) return;
                                  try {
                                    const residuo = Math.max(0, (parseFloat(trial.importo_annuale||0)||0) - (parseFloat(trial.importo_trial||0)||0));
                                    const newFields = {
                                      ...emptyFields,
                                      ...fields,
                                      importo_totale: String(residuo),
                                      piano_pagamento: {
                                        tipo: trial.modalita_annuale || "rateale",
                                        acconto: { importo: "", data: "", paid: false, paid_at: "" },
                                        rate: (trial.rate_annuale || []).map((r) => ({ ...r })),
                                        trial: { ...emptyFields.piano_pagamento.trial },
                                      },
                                      linked_trial_contract_id: initial.id,
                                      notes_generali: `Contratto annuale generato da conversione trial (${initial.id})`,
                                    };
                                    await api.post("/contratti", { artist_name: artistName, fields: newFields, status: "signed" });
                                    toast.success("Contratto annuale creato e collegato");
                                    onClose();
                                  } catch { toast.error("Errore creazione contratto annuale"); }
                                }}
                                className="px-3 py-2 border-2 border-[#0A0A0A] text-[10px] uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:text-white"
                                data-testid="btn-crea-contratto-annuale"
                              >
                                <PlusCircle size={12} className="inline mr-1"/> Crea contratto annuale collegato
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Repository file (solo per contratti già salvati) */}
          {initial?.id && (
            <ContractVersionsPanel
              contractId={initial.id}
              artistName={artistName || fields.nome_cognome}
              initialVersions={initial.versions || []}
            />
          )}

          {/* Status */}
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Status</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(STATUS_META).map(([k, m]) => (
                <button key={k} onClick={()=>setStatus(k)} className={`px-3 py-1 text-[10px] uppercase tracking-widest border-2 ${status===k ? m.cls+" border-transparent" : "border-[#0A0A0A]/30 text-[#0A0A0A]/60"}`} data-testid={`st-${k}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t-2 border-[#0A0A0A] p-4 bg-[#F2F2EC] flex flex-wrap items-center justify-end gap-2">
          {initial?.id && (
            <WhatsAppSendContract contract={{ ...initial, artist_name: artistName, fields }} size="lg" onSent={() => { toast.success("Bozza tracciata come inviata"); }} />
          )}
          {initial?.id && (initial.versions || []).length > 0 && (
            <button
              onClick={async () => {
                if (!window.confirm("Rigenera dal template originale? Il download NON sostituisce le versioni caricate — le trovi sempre nel repository sopra.")) return;
                try {
                  const res = await api.post(`/contratti/${initial.id}/generate-from-template?format=pdf`, {}, { responseType: "blob" });
                  const isFallback = res.headers?.["x-contract-fallback"] === "docx";
                  const ext = isFallback ? "docx" : "pdf";
                  const mime = ext === "docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf";
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
                  const a = document.createElement("a");
                  a.href = url; a.download = `contratto_TEMPLATE_${(artistName || "artista").replace(/[^a-z0-9]+/gi,"_")}.${ext}`; a.click();
                  window.URL.revokeObjectURL(url);
                  toast.info("Rigenerato dal template (le versioni caricate restano intatte)");
                } catch { toast.error("Errore rigenerazione"); }
              }}
              className="px-3 py-2 border-2 border-[#0A0A0A]/50 text-[10px] uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A]"
              title="Scarica il contratto rigenerato dal template (senza le tue integrazioni) — le versioni caricate restano"
              data-testid="regenerate-from-template"
            >
              Rigenera dal template
            </button>
          )}
          <button onClick={()=>save(false)} disabled={saving} className="px-4 py-2 border-2 border-[#0A0A0A] text-xs uppercase tracking-widest disabled:opacity-60" data-testid="save-only">
            <Save size={14} className="inline mr-1"/> Salva
          </button>
          <button onClick={()=>save(true, "docx")} disabled={saving} className="px-4 py-2 border-2 border-[#0A0A0A] text-xs uppercase tracking-widest disabled:opacity-60 hover:bg-[#0A0A0A] hover:text-[#FAFAF7]" data-testid="save-docx">
            {saving ? <Loader2 size={14} className="animate-spin inline"/> : <FileText size={14} className="inline mr-1"/>}
            Salva e scarica Word
          </button>
          <button onClick={()=>save(true, "pdf")} disabled={saving} className="btn-cta disabled:opacity-60" data-testid="save-pdf">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>}
            Salva e scarica PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// Merge stored fields with defaults, coercing legacy scalar payment fields into the new structure
function mergeFieldsWithDefaults(stored) {
  const s = stored || {};
  const merged = { ...emptyFields, ...s };
  // Deep-merge piano_pagamento
  const storedPiano = s.piano_pagamento || {};
  const storedTrial = storedPiano.trial || {};
  // Infer tipo se non salvato esplicitamente (backwards-compat)
  let tipo = storedPiano.tipo;
  if (!tipo) {
    if (storedTrial.importo_trial) tipo = "trial_annuale";
    else if ((storedPiano.rate || []).length > 0) tipo = "rateale";
    else tipo = "unica";
  }
  merged.piano_pagamento = {
    tipo,
    acconto: { ...emptyFields.piano_pagamento.acconto, ...(storedPiano.acconto || {}) },
    rate: Array.isArray(storedPiano.rate) ? storedPiano.rate : [],
    trial: { ...emptyFields.piano_pagamento.trial, ...storedTrial },
  };
  // Ensure services_selected is array
  if (!Array.isArray(merged.services_selected)) merged.services_selected = [];
  // Legacy fallback: if there was old free-text `elenco_servizi`, keep it as services_extra prefill
  if (!merged.services_extra && s.elenco_servizi) merged.services_extra = s.elenco_servizi;
  return merged;
}

// -----------------------------------------------------------------------------
// ContractVersionsPanel — repository di versioni file caricate dopo modifiche manuali
// -----------------------------------------------------------------------------
function ContractVersionsPanel({ contractId, artistName, initialVersions }) {
  const [versions, setVersions] = useState(initialVersions || []);
  const [uploading, setUploading] = useState(false);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!contractId) return;
    api.get(`/contratti/${contractId}/versions`).then((r) => setVersions(r.data || [])).catch(() => {});
  }, [contractId]);

  const humanSize = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const handleFile = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["docx", "pdf"].includes(ext)) {
      toast.error("Formato non supportato — solo .docx o .pdf");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (note.trim()) form.append("note", note.trim());
      const r = await api.post(`/contratti/${contractId}/upload-version`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVersions((v) => [...v, r.data.version]);
      setNote("");
      setShowNote(false);
      toast.success(`Versione v${r.data.versions_count} caricata`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadVersion = async (v, idx) => {
    try {
      const res = await api.get(`/contratti/${contractId}/versions/${v.id}/download`, { responseType: "blob" });
      const ext = v.ext || "docx";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: v.content_type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto_${(artistName || "artista").replace(/[^a-z0-9]+/gi, "_")}_v${idx + 1}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download");
    }
  };

  const removeVersion = async (v) => {
    if (!window.confirm(`Eliminare questa versione? La history non è recuperabile.`)) return;
    try {
      await api.delete(`/contratti/${contractId}/versions/${v.id}`);
      setVersions((all) => all.filter((x) => x.id !== v.id));
      toast.success("Versione eliminata");
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <div className="border-2 border-[#0A0A0A] bg-white" data-testid="contract-versions-panel">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b-2 border-[#0A0A0A] bg-[#F2F2EC]">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F] flex items-center gap-2">
            <History size={14} /> Repository file contratto
          </div>
          <div className="text-[10px] text-[#0A0A0A]/60 mt-0.5">
            Se hai già scaricato il contratto e aggiunto integrazioni sul PC, ricaricalo qui: la prossima volta ti restituisco la tua versione modificata invece di rigenerarla dal template.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60" data-testid="versions-count">
            {versions.length} {versions.length === 1 ? "versione" : "versioni"}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <input
          ref={fileRef}
          type="file"
          accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="version-upload-input"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="px-3 py-2 border-2 border-[#FF5A1F] bg-[#FF5A1F] text-white text-[10px] uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:border-[#0A0A0A] flex items-center gap-2 disabled:opacity-60"
            data-testid="upload-version-btn"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Caricamento..." : "Carica versione modificata (.docx / .pdf)"}
          </button>
          <button
            type="button"
            onClick={() => setShowNote((v) => !v)}
            className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 hover:text-[#FF5A1F]"
            data-testid="toggle-version-note"
          >
            {showNote ? "− Nota" : "+ Nota (facoltativa)"}
          </button>
        </div>
        {showNote && (
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Es. Aggiunta clausola su diritti audiovisivi"
            className="w-full border-2 border-[#0A0A0A] p-2 text-sm"
            data-testid="version-note-input"
          />
        )}

        {versions.length === 0 ? (
          <div className="text-xs text-[#0A0A0A]/50 italic border-2 border-dashed border-[#0A0A0A]/20 p-4 text-center">
            Nessuna versione caricata. I download useranno il template Word originale.
          </div>
        ) : (
          <div className="space-y-2">
            {versions.map((v, idx) => {
              const isLatest = idx === versions.length - 1;
              return (
                <div
                  key={v.id}
                  className={`flex items-start gap-3 p-3 border-2 ${isLatest ? "border-[#FF5A1F] bg-[#FFF5EF]" : "border-[#0A0A0A]/20"}`}
                  data-testid={`version-${v.id}`}
                >
                  <div className="w-9 h-9 shrink-0 border-2 border-[#0A0A0A] bg-white flex items-center justify-center">
                    <FileText size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]">v{idx + 1}</span>
                      {isLatest && (
                        <span className="text-[9px] uppercase tracking-widest bg-[#FF5A1F] text-white px-1.5 py-0.5 font-bold">Corrente</span>
                      )}
                      <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">
                        {v.ext} · {humanSize(v.size)}
                      </span>
                    </div>
                    <div className="text-sm text-[#0A0A0A]/85 mt-0.5 truncate" title={v.filename}>{v.filename}</div>
                    <div className="text-[10px] text-[#0A0A0A]/60 mt-0.5">
                      {new Date(v.uploaded_at).toLocaleString("it-IT")}
                      {v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ""}
                    </div>
                    {v.note && (
                      <div className="text-xs text-[#0A0A0A]/80 italic mt-1 border-l-2 border-[#FF5A1F] pl-2">"{v.note}"</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => downloadVersion(v, idx)}
                      className="p-2 border-2 border-[#0A0A0A]/30 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"
                      title="Scarica questa versione"
                      data-testid={`version-download-${v.id}`}
                    >
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => removeVersion(v)}
                      className="p-2 border-2 border-[#0A0A0A]/30 hover:border-red-500 hover:text-red-500"
                      title="Elimina versione"
                      data-testid={`version-delete-${v.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function WhatsAppSendContract({ contract, onSent, size = "sm" }) {
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const senderName = (user?.name || "").split(" ")[0] || "";
  const phone = contract?.fields?.telefono || "";
  const clean = (phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
  const alreadySent = !!contract?.sent_at;
  const sentCount = contract?.sent_count || 0;

  const send = async () => {
    if (!clean) {
      toast.error("Cellulare mancante — aggiungilo nel contratto prima di inviare");
      return;
    }
    if (alreadySent && !window.confirm(`Bozza già inviata ${sentCount}x. Rispedire?`)) return;
    setSending(true);
    try {
      // 1) Generate & auto-download PDF (fallback DOCX if PDF conversion fails)
      const res = await api.post(`/contratti/${contract.id}/generate-pdf`, {}, { responseType: "blob" });
      const isFallback = res.headers?.["x-contract-fallback"] === "docx";
      const ext = isFallback ? "docx" : "pdf";
      const mime = isFallback
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/pdf";
      const safeName = (contract.artist_name || "artista").replace(/[^a-z0-9]+/gi, "_");
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto_${safeName}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      // 2) Open WhatsApp with pre-filled message (formula richiesta dall'utente)
      const firstName = (contract.artist_name || "").split(" ")[0] || "";
      const msg = `Ciao ${firstName},\ncome da accordi ti invio la bozza del contratto Perla Music.\nDovrai solo inserire i tuoi dati e dirmi la modalità di pagamento a te più conveniente.\nA presto,\n${senderName}`;
      const waUrl = `https://wa.me/${clean}?text=${encodeURIComponent(msg)}`;
      window.open(waUrl, "_blank", "noopener");
      // 3) Track "sent"
      await api.post(`/contratti/${contract.id}/mark-sent`, { channel: "whatsapp" });
      toast.success(`Bozza inviata a ${contract.artist_name}${isFallback ? " (in formato Word)" : ""}`);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio WhatsApp");
    } finally {
      setSending(false);
    }
  };

  const btnCls = size === "lg"
    ? "px-4 py-2 border-2 text-xs uppercase tracking-widest font-bold flex items-center gap-2"
    : "p-2 border-2 flex items-center gap-1";

  const stateCls = alreadySent
    ? "border-green-600 bg-green-50 text-green-700 hover:bg-green-600 hover:text-white"
    : "border-[#0A0A0A]/30 hover:border-green-600 hover:text-green-600";

  return (
    <button
      type="button"
      onClick={send}
      disabled={sending || !clean}
      className={`${btnCls} ${stateCls} disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}
      title={
        !clean ? "Cellulare mancante — aggiungilo nel contratto"
        : alreadySent ? `Bozza inviata${sentCount > 1 ? ` ${sentCount}x` : ""} — click per rispedire`
        : "Invia bozza contratto via WhatsApp"
      }
      data-testid={`wa-send-${contract.id}`}
    >
      {sending ? <Loader2 size={14} className="animate-spin" /> : alreadySent ? <Check size={14} /> : <MessageCircle size={14} />}
      {size === "lg" && (sending ? "Invio..." : alreadySent ? `Rispedisci bozza${sentCount > 1 ? ` (${sentCount}x)` : ""}` : "Invia via WhatsApp")}
    </button>
  );
}


function PaidToggle({ paid, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!paid)}
      className={`mt-1 w-full border-2 p-2 text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-1 ${paid ? "bg-green-600 border-green-700 text-white" : "border-[#0A0A0A]/30 text-[#0A0A0A]/60 hover:border-green-600 hover:text-green-600"}`}
      data-testid={testId}
    >
      {paid ? <><Check size={12} /> Incassata</> : "Non incassata"}
    </button>
  );
}

function CommissionBadge({ fields }) {
  const s = commissionSummary(fields);
  if (!s.total_scheduled) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap text-[10px]" data-testid="commission-badge">
      <span className="uppercase tracking-widest text-[#0A0A0A]/50">Provv.</span>
      <span className="border-2 border-green-600 bg-green-600 text-white px-2 py-1 font-bold" title={`${s.total_paid.toFixed(2)}€ incassati × ${s.pct}%`}>
        Maturata: {money(s.commission_earned)}
      </span>
      <span className="border-2 border-[#0A0A0A]/30 px-2 py-1 text-[#0A0A0A]/70" title={`${s.total_pending.toFixed(2)}€ ancora da incassare × ${s.pct}%`}>
        In attesa: {money(s.commission_pending)}
      </span>
      <span className="text-[#0A0A0A]/40">/ {money(s.commission_total)} totale</span>
    </div>
  );
}

function CommissionSummaryBar({ contracts }) {
  const totals = (contracts || []).reduce(
    (acc, c) => {
      const s = commissionSummary(c.fields || {});
      acc.earned += s.commission_earned;
      acc.pending += s.commission_pending;
      acc.total += s.commission_total;
      acc.paid += s.total_paid;
      acc.pending_amt += s.total_pending;
      return acc;
    },
    { earned: 0, pending: 0, total: 0, paid: 0, pending_amt: 0 }
  );
  if (!totals.total) return null;
  return (
    <div className="mb-6 border-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7] p-5" data-testid="commission-summary-bar">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF5A1F] font-bold mb-1">Riepilogo provvigioni</div>
      <div className="text-[10px] text-[#FAFAF7]/40 mb-3">Include solo contratti con status <b>FIRMATO</b> — bozze e in attesa non sono conteggiate</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FAFAF7]/50">Incassato</div>
          <div className="font-display text-2xl md:text-3xl text-[#FAFAF7] mt-1">{money(totals.paid)}</div>
          <div className="text-[10px] text-[#FAFAF7]/40 mt-0.5">totale rate marcate come incassate</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FAFAF7]/50">Provvigione maturata</div>
          <div className="font-display text-2xl md:text-3xl text-green-400 mt-1">{money(totals.earned)}</div>
          <div className="text-[10px] text-[#FAFAF7]/40 mt-0.5">già tua, incassata</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FAFAF7]/50">Ancora da incassare</div>
          <div className="font-display text-2xl md:text-3xl text-[#FAFAF7] mt-1">{money(totals.pending_amt)}</div>
          <div className="text-[10px] text-[#FAFAF7]/40 mt-0.5">rate non ancora marcate</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FAFAF7]/50">Provvigione in attesa</div>
          <div className="font-display text-2xl md:text-3xl text-[#FF5A1F] mt-1">{money(totals.pending)}</div>
          <div className="text-[10px] text-[#FAFAF7]/40 mt-0.5">quando incasseranno le rate</div>
        </div>
      </div>
    </div>
  );
}


function OverdueRatesAlert({ contracts }) {
  const [open, setOpen] = useState(true);
  const overdueByContract = (contracts || [])
    .map((c) => ({ contract: c, overdue: collectOverdueRates(c) }))
    .filter((x) => x.overdue.length > 0);
  if (overdueByContract.length === 0) return null;
  const totalOverdueAmount = overdueByContract.reduce(
    (sum, x) => sum + x.overdue.reduce((s, r) => s + (parseFloat(r.importo) || 0), 0),
    0
  );
  const totalCount = overdueByContract.reduce((s, x) => s + x.overdue.length, 0);
  return (
    <div className="mb-6 border-2 border-red-600 bg-red-50" data-testid="overdue-rates-alert">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 hover:bg-red-100"
        data-testid="overdue-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border-2 border-red-600 bg-red-600 text-white flex items-center justify-center animate-pulse">
            <AlertTriangle size={18} />
          </div>
          <div className="text-left">
            <div className="font-display text-lg uppercase text-red-700">
              {totalCount} rat{totalCount === 1 ? "a scaduta" : "e scadute"} da incassare
            </div>
            <div className="text-[10px] uppercase tracking-widest text-red-600/80">
              Totale: {money(totalOverdueAmount)} · {overdueByContract.length} contratt{overdueByContract.length === 1 ? "o" : "i"}
            </div>
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-red-600" /> : <ChevronDown size={16} className="text-red-600" />}
      </button>
      {open && (
        <div className="border-t-2 border-red-600 divide-y divide-red-200">
          {overdueByContract.map(({ contract, overdue }) => (
            <div key={contract.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`overdue-${contract.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm uppercase text-[#0A0A0A]">{contract.artist_name}</div>
                <div className="text-[10px] text-red-700 flex flex-wrap gap-2 mt-1">
                  {overdue.map((r, i) => (
                    <span key={i} className="border border-red-400 bg-white px-1.5 py-0.5">
                      {r.label} · {r.data} · {money(parseFloat(r.importo) || 0)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Modale "Trova duplicati contratti"
// Cerca gruppi di contratti con artist_name simile (fuzzy) e permette di fonderli
// scegliendo quale è il primary. Gli altri vengono archiviati (non persi).
// -----------------------------------------------------------------------------
function DuplicatesModal({ onClose, onMerged }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [merging, setMerging] = useState(null); // id del gruppo che stiamo fondendo
  // Per ogni gruppo memorizziamo: primary_id + set di other_ids selezionati
  const [selections, setSelections] = useState({}); // { groupIdx: { primary: id, others: Set(ids) } }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/contratti/duplicates");
      const gs = r.data?.groups || [];
      setGroups(gs);
      // Default: primary = suggested, others = tutti gli altri
      const sel = {};
      gs.forEach((g, i) => {
        const otherIds = new Set(g.contracts.filter((c) => c.id !== g.suggested_primary_id).map((c) => c.id));
        sel[i] = { primary: g.suggested_primary_id, others: otherIds };
      });
      setSelections(sel);
    } catch (e) {
      toast.error("Errore caricamento duplicati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setPrimary = (gIdx, id) => {
    setSelections((s) => {
      const cur = s[gIdx] || { primary: "", others: new Set() };
      const others = new Set(groups[gIdx].contracts.filter((c) => c.id !== id).map((c) => c.id));
      // preserviamo eventuali deselezioni
      cur.others.forEach((oid) => { if (oid === id) others.delete(id); });
      return { ...s, [gIdx]: { primary: id, others } };
    });
  };

  const toggleOther = (gIdx, id) => {
    setSelections((s) => {
      const cur = s[gIdx];
      if (!cur) return s;
      const others = new Set(cur.others);
      if (others.has(id)) others.delete(id); else others.add(id);
      return { ...s, [gIdx]: { ...cur, others } };
    });
  };

  const doMerge = async (gIdx) => {
    const sel = selections[gIdx];
    if (!sel || !sel.primary || sel.others.size === 0) {
      toast.error("Seleziona almeno un altro contratto da fondere");
      return;
    }
    setMerging(gIdx);
    try {
      const r = await api.post("/contratti/merge", {
        primary_id: sel.primary,
        other_ids: Array.from(sel.others),
      });
      toast.success(`Uniti ${r.data.merged_count} contratti nel principale`);
      // Rimuovi il gruppo appena unito dalla lista
      setGroups((gs) => gs.filter((_, i) => i !== gIdx));
      // aggiorna selections rimuovendo l'indice
      setSelections((s) => {
        const ns = { ...s };
        delete ns[gIdx];
        // reindex non necessario perché usiamo i valori originali di gs
        return ns;
      });
      onMerged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore fusione");
    } finally {
      setMerging(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="duplicates-modal">
      <div className="bg-[#FAFAF7] border-2 border-[#0A0A0A] max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b-2 border-[#0A0A0A] p-4 flex items-center justify-between sticky top-0 bg-[#FAFAF7] z-10">
          <div>
            <div className="font-display text-2xl uppercase text-[#0A0A0A]">Trova duplicati</div>
            <div className="text-xs text-[#0A0A0A]/60 mt-1">
              Contratti con nome artista simile — scegli il "principale" e fondi gli altri
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:text-[#FF5A1F]" data-testid="dup-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="animate-spin inline" size={20} /></div>
          ) : groups.length === 0 ? (
            <div className="border-2 border-dashed border-green-500 bg-green-50 p-8 text-center">
              <Check size={32} className="mx-auto mb-2 text-green-600" />
              <div className="font-display text-lg uppercase text-green-700">Nessun duplicato</div>
              <div className="text-xs text-[#0A0A0A]/60 mt-1">Tutti i contratti hanno nomi distinti</div>
            </div>
          ) : (
            groups.map((g, gIdx) => {
              const sel = selections[gIdx];
              if (!sel) return null;
              return (
                <div key={gIdx} className="border-2 border-[#FF5A1F] bg-white p-4" data-testid={`dup-group-${gIdx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-display text-lg uppercase">{g.artist_display}</div>
                      <div className="text-[10px] text-[#0A0A0A]/60">{g.count} contratti trovati con nome simile</div>
                    </div>
                  </div>
                  <div className="space-y-2 mb-3">
                    {g.contracts.map((c) => {
                      const isPrimary = sel.primary === c.id;
                      const isOther = sel.others.has(c.id);
                      return (
                        <div key={c.id} className={`border-2 p-3 flex items-start gap-3 ${isPrimary ? "border-[#FF5A1F] bg-[#FFF5EF]" : "border-[#0A0A0A]/20"}`} data-testid={`dup-contract-${c.id}`}>
                          <div className="pt-1">
                            <input
                              type="radio"
                              name={`primary-${gIdx}`}
                              checked={isPrimary}
                              onChange={() => setPrimary(gIdx, c.id)}
                              className="mr-1"
                              data-testid={`dup-primary-${c.id}`}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm">{c.artist_name}</span>
                              <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${STATUS_META[c.status]?.cls || "bg-gray-200"}`}>
                                {STATUS_META[c.status]?.label || c.status}
                              </span>
                              {c.is_manager && <span className="text-[9px] bg-purple-600 text-white px-1 py-0.5">MANAGER</span>}
                              {isPrimary && <span className="text-[9px] uppercase font-bold text-[#FF5A1F]">▸ Principale</span>}
                            </div>
                            <div className="text-[10px] text-[#0A0A0A]/60 mt-1 flex flex-wrap gap-2">
                              <span>{c.fields_count} campi</span>
                              {c.versions_count > 0 && <span>· {c.versions_count} versioni</span>}
                              {c.importo_totale && <span>· € {c.importo_totale}</span>}
                              {c.sent_at && <span>· inviato {new Date(c.sent_at).toLocaleDateString("it-IT")}</span>}
                            </div>
                          </div>
                          {!isPrimary && (
                            <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isOther}
                                onChange={() => toggleOther(gIdx, c.id)}
                                data-testid={`dup-other-${c.id}`}
                              />
                              <span>Fondi</span>
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] text-[#0A0A0A]/60">
                      Il principale <b>sopravvive</b> · gli altri vengono <b>archiviati</b> (non persi)
                    </div>
                    <button
                      onClick={() => doMerge(gIdx)}
                      disabled={merging === gIdx || sel.others.size === 0}
                      className="px-3 py-1.5 border-2 border-[#FF5A1F] bg-[#FF5A1F] text-white text-[10px] uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:border-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      data-testid={`dup-merge-btn-${gIdx}`}
                    >
                      {merging === gIdx ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                      Fondi ({sel.others.size + 1})
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="border-t-2 border-[#0A0A0A] p-4 flex items-center justify-end gap-2 sticky bottom-0 bg-[#FAFAF7]">
          <button
            onClick={onClose}
            className="px-4 py-2 border-2 border-[#0A0A0A]/30 text-sm uppercase tracking-widest font-bold hover:border-[#0A0A0A]"
            data-testid="dup-done-btn"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

